import React, { useState } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  alpha,
  Divider,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { formatLabel } from './memberSummaryHelpers';
import { clientGroupByOptions, tpaGroupByOptions, DUMMY_FIELDS } from '../constants';

export const RollupConfiguration = ({
  sumFieldCategories,
  filterType,
  rollupConfig,
  onGroupByChange,
  onSumFieldsChange,
  sumDisabled = false,
  disabledFields,
}) => {
  const theme = useTheme();

  const groupByFields = rollupConfig?.group_by_fields || [];
  const sumFields = rollupConfig?.sum_fields || [];

  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingGroupBy, setPendingGroupBy] = useState(null);

  const isFieldDisabled = (fieldName) => Array.isArray(disabledFields) && disabledFields.includes(fieldName);
  const handleGroupByChange = (event) => {
    const newValue = event.target.value;
    const hasSumFieldsSelected = sumFields.length > 0;

    if (hasSumFieldsSelected && JSON.stringify(newValue) !== JSON.stringify(groupByFields)) {
      setPendingGroupBy(newValue);
      setDialogOpen(true);
    } else {
      onGroupByChange(newValue, false);
    }
  };

  const handleDialogConfirm = () => {
    if (pendingGroupBy) {
      onGroupByChange(pendingGroupBy, true);
    }
    setDialogOpen(false);
    setPendingGroupBy(null);
  };

  const handleDialogCancel = () => {
    setDialogOpen(false);
    setPendingGroupBy(null);
  };

  const activeOptions = filterType === 'client' ? clientGroupByOptions : tpaGroupByOptions;
  const isSumDisabled = sumDisabled || groupByFields.length === 0;

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ mb: 3, maxWidth: '400px' }}>
        <FormControl fullWidth size="small">
          <InputLabel>Roll up by</InputLabel>
          <Select
            value={groupByFields}
            label="Roll up by"
            onChange={handleGroupByChange}
            renderValue={(selected) => selected.map((field) => formatLabel(field)).join(', ')}
          >
            {activeOptions.map((option) => (
              <MenuItem key={option.label} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {sumFieldCategories.map((category, catIndex) => (
        <Box
          key={category.id}
          sx={{
            display: 'flex',
            alignItems: 'stretch',
            backgroundColor: catIndex % 2 !== 0 ? alpha(theme.palette.action.hover, 0.04) : 'transparent',
            borderBottom: catIndex < sumFieldCategories.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
          }}
        >
          <Box sx={{ minWidth: 170, p: 1, fontWeight: 'bold', borderRight: `1px solid black`, flexShrink: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 'inherit' }}>
              {category.name}
            </Typography>
          </Box>

          <Box sx={{ flexGrow: 1, p: 1 }}>
            {Array.isArray(category.groups) ? (
              category.groups.map((group, groupIndex) => (
                <Box
                  key={groupIndex}
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    mb: groupIndex < category.groups.length - 1 ? 1.5 : 0,
                  }}
                >
                  {group.map((fieldName) => (
                    <Box
                      key={fieldName}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        width: 110,
                        minWidth: 110,
                        p: '4px',
                      }}
                    >
                      <Checkbox
                        checked={sumFields.includes(fieldName)}
                        onChange={(e) => onSumFieldsChange(fieldName, e.target.checked)}
                        name={fieldName}
                        size="small"
                        disabled={isFieldDisabled(fieldName) || groupByFields.includes(fieldName)}
                        sx={{ padding: '0 0 2px 0' }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          textAlign: 'center',
                          lineHeight: 1.1,
                          wordBreak: 'break-word',
                          fontSize: '0.75rem',
                          color: theme.palette.text.secondary,
                        }}
                      >
                        {formatLabel(fieldName)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ))
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {category.id === 'financials' ? (
                  <>
                    {(category.fields ?? [])
                      .filter((f) => !DUMMY_FIELDS.includes(f))
                      .map((fieldName) => (
                        <Box
                          key={fieldName}
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            width: 110,
                            minWidth: 110,
                            p: '4px',
                          }}
                        >
                          <Checkbox
                            checked={sumFields.includes(fieldName)}
                            onChange={(e) => onSumFieldsChange(fieldName, e.target.checked)}
                            name={fieldName}
                            size="small"
                            disabled={isFieldDisabled(fieldName) || groupByFields.includes(fieldName)}
                            sx={{ padding: '0 0 2px 0' }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              textAlign: 'center',
                              lineHeight: 1.1,
                              wordBreak: 'break-word',
                              fontSize: '0.75rem',
                              color: theme.palette.text.secondary,
                            }}
                          >
                            {formatLabel(fieldName)}
                          </Typography>
                        </Box>
                      ))}
                    <Divider sx={{ flexBasis: '100%', my: 1 }} />
                    {(category.fields ?? [])
                      .filter((f) => DUMMY_FIELDS.includes(f))
                      .map((fieldName) => (
                        <Box
                          key={fieldName}
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            width: 110,
                            minWidth: 110,
                            p: '4px',
                          }}
                        >
                          <Checkbox
                            checked={sumFields.includes(fieldName)}
                            onChange={(e) => onSumFieldsChange(fieldName, e.target.checked)}
                            name={fieldName}
                            size="small"
                            disabled={isFieldDisabled(fieldName) || groupByFields.includes(fieldName)}
                            sx={{ padding: '0 0 2px 0' }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              textAlign: 'center',
                              lineHeight: 1.1,
                              wordBreak: 'break-word',
                              fontSize: '0.75rem',
                              color: theme.palette.text.secondary,
                            }}
                          >
                            {formatLabel(fieldName)}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: '0.7rem',
                              color: theme.palette.text.disabled,
                              textAlign: 'center',
                              lineHeight: 1,
                              fontStyle: 'italic',
                            }}
                          >
                            (null)
                          </Typography>
                        </Box>
                      ))}
                  </>
                ) : (
                  (category.fields ?? []).map((fieldName) => (
                    <Box
                      key={fieldName}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        width: 110,
                        minWidth: 110,
                        p: '4px',
                      }}
                    >
                      <Checkbox
                        checked={sumFields.includes(fieldName)}
                        onChange={(e) => onSumFieldsChange(fieldName, e.target.checked)}
                        name={fieldName}
                        size="small"
                        disabled={isFieldDisabled(fieldName) || groupByFields.includes(fieldName)}
                        sx={{ padding: '0 0 2px 0' }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          textAlign: 'center',
                          lineHeight: 1.1,
                          wordBreak: 'break-word',
                          fontSize: '0.75rem',
                          color: theme.palette.text.secondary,
                        }}
                      >
                        {formatLabel(fieldName)}
                      </Typography>
                    </Box>
                  ))
                )}
              </Box>
            )}
          </Box>
        </Box>
      ))}

      <Dialog
        open={dialogOpen}
        onClose={handleDialogCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Confirm Change</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Changing the 'Roll up by' field will clear your current selections in 'Sum Fields'. Are you sure you want to
            continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogCancel}>Cancel</Button>
          <Button onClick={handleDialogConfirm} variant="contained" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
